import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Copy,
  Check,
  KeyRound,
  Loader2,
  ShieldAlert,
  Wand2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

/**
 * Two-step password-reset dialog for the Platform Admin.
 *
 * Step 1 — "form": choose auto-generate vs. type a custom password;
 *   show a strong warning that current sessions will be revoked and
 *   the plaintext will only be shown once.
 * Step 2 — "reveal": display the new plaintext with a big copy button.
 *   Closing the dialog wipes the password from React state so it can
 *   never be re-read.
 *
 * Backend contract: `POST /platform/users/:id/reset-password`
 *   body `{}` → auto-generate
 *   body `{"password": "..."}` → admin-supplied (min 8 chars)
 *   response `{"password": "...", "generated": bool}`
 */
export default function UserPasswordResetDialog({ open, onOpenChange, user }) {
  const [mode, setMode] = useState("auto"); // "auto" | "custom"
  const [customPassword, setCustomPassword] = useState("");
  const [phase, setPhase] = useState("form"); // "form" | "reveal"
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset every dialog open so a re-open never leaks the previous
      // plaintext to a different admin session on the same tab.
      setMode("auto");
      setCustomPassword("");
      setPhase("form");
      setNewPassword("");
      setCopied(false);
      setBusy(false);
    }
  }, [open]);

  if (!user) return null;

  const submit = async () => {
    const payload = mode === "custom" && customPassword.trim()
      ? { password: customPassword.trim() }
      : {};
    if (mode === "custom" && (payload.password?.length ?? 0) < 8) {
      toast.error("Şifre en az 8 karakter olmalı");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/platform/users/${user.id}/reset-password`,
        payload
      );
      setNewPassword(data.password);
      setPhase("reveal");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (e.g. non-HTTPS) — select the input as a fallback.
      const el = document.getElementById("reset-password-value");
      if (el) el.select();
    }
  };

  const close = () => {
    // Wipe plaintext from memory before the dialog closes.
    setNewPassword("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : (v) => (v ? null : close())}>
      <DialogContent data-testid="user-reset-password-dialog">
        {phase === "form" && (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-amber-100">
                <KeyRound className="h-5 w-5 text-amber-700" />
              </div>
              <DialogTitle>Şifreyi sıfırla</DialogTitle>
              <DialogDescription>
                <span className="font-semibold text-zinc-900">
                  {user.name || user.email}
                </span>{" "}
                için yeni bir şifre oluştur. Kullanıcının mevcut oturumları kapatılacak
                ve tekrar giriş yapması gerekecek.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <div className="flex items-start gap-2">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Şifreler DB&apos;de bcrypt hash olarak saklanır — mevcut şifreyi
                  <b> görüntülemek teknik olarak mümkün değildir</b>. Yeni bir
                  şifre üretilir ve <b>yalnızca bir kez</b> gösterilir.
                </span>
              </div>
            </div>

            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2 rounded-lg border border-zinc-200 p-3">
                <input
                  type="radio"
                  id="reset-mode-auto"
                  name="reset-mode"
                  checked={mode === "auto"}
                  onChange={() => setMode("auto")}
                  className="h-4 w-4 accent-violet-600"
                  data-testid="reset-mode-auto"
                />
                <label
                  htmlFor="reset-mode-auto"
                  className="flex flex-1 cursor-pointer items-center gap-2 text-sm"
                >
                  <Wand2 className="h-4 w-4 text-violet-600" />
                  <span>
                    <span className="font-medium">Otomatik oluştur</span>
                    <span className="ml-1 text-zinc-500">— güvenli, ~13 karakter</span>
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-zinc-200 p-3">
                <input
                  type="radio"
                  id="reset-mode-custom"
                  name="reset-mode"
                  checked={mode === "custom"}
                  onChange={() => setMode("custom")}
                  className="mt-1 h-4 w-4 accent-violet-600"
                  data-testid="reset-mode-custom"
                />
                <label
                  htmlFor="reset-mode-custom"
                  className="flex flex-1 cursor-pointer flex-col gap-1.5 text-sm"
                >
                  <span>
                    <span className="font-medium">Manuel gir</span>
                    <span className="ml-1 text-zinc-500">— minimum 8 karakter</span>
                  </span>
                  <Input
                    type="text"
                    value={customPassword}
                    onChange={(e) => {
                      setMode("custom");
                      setCustomPassword(e.target.value);
                    }}
                    placeholder="yeni-sifre-buraya"
                    disabled={busy || mode !== "custom"}
                    data-testid="reset-custom-password"
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={close}
                disabled={busy}
                data-testid="reset-password-cancel"
              >
                Vazgeç
              </Button>
              <Button
                onClick={submit}
                disabled={busy || (mode === "custom" && customPassword.trim().length < 8)}
                data-testid="reset-password-submit"
                className="bg-amber-600 text-white hover:bg-amber-700"
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sıfırlanıyor…
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" /> Şifreyi Sıfırla
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === "reveal" && (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100">
                <Check className="h-5 w-5 text-emerald-700" />
              </div>
              <DialogTitle>Yeni şifre oluşturuldu</DialogTitle>
              <DialogDescription>
                Bu şifreyi <b>{user.email}</b> kullanıcısına ilet. Aşağıdaki
                değeri kopyala — bu ekranı kapattığında bir daha görüntüleyemezsin.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-2">
              <Label htmlFor="reset-password-value" className="text-xs">
                Yeni şifre
              </Label>
              <div className="flex gap-2">
                <Input
                  id="reset-password-value"
                  data-testid="reset-password-value"
                  value={newPassword}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  className="font-mono text-sm tracking-wide"
                />
                <Button
                  variant="outline"
                  onClick={copy}
                  data-testid="reset-password-copy"
                  className="shrink-0"
                >
                  {copied ? (
                    <>
                      <Check className="mr-1.5 h-4 w-4 text-emerald-600" /> Kopyalandı
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1.5 h-4 w-4" /> Kopyala
                    </>
                  )}
                </Button>
              </div>
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Bu değer sadece bir kez gösterilir. Kaybedilirse tekrar
                    sıfırlamak gerekir. Kullanıcının tüm oturumları kapatıldı.
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={close}
                data-testid="reset-password-done"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                Kapat
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
